import { useEffect } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { omanSelectedShippingMethodAtom, omanCartHasSubscriptionAtom } from '../stores/oman.store'
import type { TOmanShippingMethodId } from '../types/oman.types'

export type OmanShippingMethodOption = {
    id: TOmanShippingMethodId
    labelKey: string
}

export const OMAN_SHIPPING_METHODS: OmanShippingMethodOption[] = [
    { id: 'delivery', labelKey: 'economy' },
    { id: 'pickup', labelKey: 'pickup_oman_office' }
]

/**
 * Normalises the API's `shipment_options` string (which may be `'delivery'`,
 * `'pickup'`, or absent) to one of our internal ids. Falls back to `delivery`
 * by default when the value is missing or unrecognised.
 */
export const resolveOmanShippingMethod = (shipmentOptions: string | undefined, fallback: TOmanShippingMethodId = 'delivery'): TOmanShippingMethodId => {
    if (shipmentOptions === 'pickup') return 'pickup'
    if (shipmentOptions === 'delivery') return 'delivery'
    return fallback
}

interface UseAvailableShippingMethodsOptions {
    /** Optional side-effect to run when the auto-switch from pickup -> delivery
     *  fires (e.g. propagate the new selection to the API). */
    onAutoSwitch?: (next: TOmanShippingMethodId) => void
}

/**
 * Encapsulates the shared rules for the Oman shipping method radios:
 *   - Hide `pickup` when the cart contains a subscription item.
 *   - Auto-switch a previously chosen `pickup` to `delivery` if a subscription
 *     gets added later, so the radio group never renders with no selection
 *     (and the next API call doesn't post a now-invalid `pickup`).
 */
export const useAvailableShippingMethods = ({ onAutoSwitch }: UseAvailableShippingMethodsOptions = {}) => {
    const [selectedMethod, setSelectedMethod] = useAtom(omanSelectedShippingMethodAtom)
    const hasSubscription = useAtomValue(omanCartHasSubscriptionAtom)

    const availableMethods = OMAN_SHIPPING_METHODS.filter(m => !(hasSubscription && m.id === 'pickup'))

    useEffect(() => {
        if (hasSubscription && selectedMethod === 'pickup') {
            setSelectedMethod('delivery')
            onAutoSwitch?.('delivery')
        }
    }, [hasSubscription, selectedMethod, setSelectedMethod, onAutoSwitch])

    return { availableMethods, selectedMethod, setSelectedMethod }
}

interface FormatShippingLabelArgs {
    id: TOmanShippingMethodId
    baseLabel: string
    freight?: number
    freeLabel: string
}

/**
 * Formats the trailing price/free suffix for a shipping method label, e.g.
 *   "Economy - 1.9 OMR"  /  "Economy - FREE"  /  "Local Pickup… - FREE"
 */
export const formatOmanShippingLabel = ({ id, baseLabel, freight, freeLabel }: FormatShippingLabelArgs) => {
    if (id === 'delivery' && freight) {
        return `${baseLabel}\u00A0-\u00A0${freight.toLocaleString('en-US')} OMR`
    }
    return `${baseLabel}\u00A0-\u00A0${freeLabel.toUpperCase()}`
}
